// ============================================================
// SecaoResultados.js — competições e resultados, área da coordenação
//
// O lançamento é em matriz: escolhida a competição, todos os atletas da
// modalidade aparecem de uma vez, agrupados por categoria, com um campo de
// colocação cada. É como a prova acontece e é como a coordenação tem a folha
// de resultados na mão, uma categoria de cada vez.
//
// Ninguém fica sem resultado. Quem não subiu ao pódio registra a colocação
// mesmo assim, para efeito comparativo, e quem não disputou recebe o código
// da situação. Campo vazio significa "ainda não lancei", não "não competiu".
//
// Todo total do relatório sai daqui. Não existe campo de "total de medalhas"
// para digitar, e é de propósito: na planilha atual esse número é preenchido
// à mão em três abas e as três divergem.
// ============================================================

import { useState, useEffect, useCallback, useMemo } from "react";
import {
  listarCompeticoes, salvarCompeticao, salvarResultados,
  removerCompeticao, rankingDeMedalhas, carregarModalidadesDoNucleo,
  carregarCadastroDoNucleo,
} from "../firebase";
import {
  categoriasDaModalidade, ordenarParaAtleta, categoriasDisponiveis,
} from "../categorias";
import {
  AZUL, AZUL_ESCURO, AZUL_CLARO, TEXTO, CINZA_ESC, BRANCO, PERIGO, AMARELO,
  FUNDO_SUAVE, BORDA,
} from "../tema";
import { Card, Btn, StatusBox } from "../componentes";
import { gerarPDFCompeticoes } from "../pdfRelatorio";
// A medalha aparece ao lado do campo enquanto se digita. É a conferência
// mais barata que existe: se o pódio da modalidade estiver configurado
// errado, quem lança percebe na primeira linha, não no relatório.
//
// Vem de competicoes.js, que é onde a ficha do atleta também lê a
// colocação. Havia uma cópia aqui, e cópia de regra de domínio em duas
// telas é a que divergem no dia em que uma modalidade muda o pódio.
import { medalhaDe, lerColocacao, rotuloDaColocacao } from "../competicoes";

// O lançamento de um atleta vem como lista, e o documento antigo vem como
// objeto. Aceitar os dois é o que deixa abrir competição lançada antes de
// 06/10/2026 sem migrar nada.
function comoLista(bruto) {
  if (!bruto) return [];
  if (Array.isArray(bruto)) return bruto.filter((x) => x && typeof x === "object");
  if (typeof bruto === "object") return [bruto];
  return [];
}

// Situações que não são colocação. Quem foi desclassificado e quem se
// lesionou na prova estiveram lá, então a participação conta; quem não
// competiu, não conta.
// Os dois códigos oferecidos na linha.
//
// Eram três: desclassificado e lesionado existiam separados e viraram
// "não classificou" por decisão da coordenação. Para o quadro os três
// diziam a mesma coisa, que é "esteve na prova e não tem colocação", e
// três botões para um fato só é escolha a mais na hora de lançar.
const SITUACOES = [
  { cod: "NC", rotulo: "Não classificou" },
  { cod: "ND", rotulo: "Não disputou" },
];


// Principal é a categoria da idade do atleta. Secundária é tudo o que ele
// disputa além dela: idade acima, absoluto e duplas.
const TIPOS = [
  { cod: "principal", rotulo: "Principal", ajuda: "A categoria da idade do atleta" },
  { cod: "secundaria", rotulo: "Secundária", ajuda: "Idade acima, absoluto ou dupla" },
];

const BOTAO_DE_LINHA = {
  border: "1.5px solid #E5E7EB", background: BRANCO, color: CINZA_ESC,
  borderRadius: 6, padding: "5px 9px", fontSize: 11.5, fontWeight: 700,
  cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
};

const DIACRITICOS = new RegExp(
  "[" + String.fromCharCode(0x300) + "-" + String.fromCharCode(0x36f) + "]",
  "g"
);

function chave(texto) {
  return String(texto || "").normalize("NFD").replace(DIACRITICOS, "")
    .toLowerCase().replace(/[^a-z0-9]/g, "");
}

function fmt(iso) {
  if (!iso) return "";
  const p = String(iso).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
}

function periodoDe(c) {
  if (!c.dataInicio) return "";
  if (!c.dataFim || c.dataFim === c.dataInicio) return fmt(c.dataInicio);
  if (c.dataInicio.slice(0, 7) === c.dataFim.slice(0, 7)) {
    return `${c.dataInicio.slice(8, 10)} a ${c.dataFim.slice(8, 10)}/${c.dataInicio.slice(5, 7)}/${c.dataInicio.slice(0, 4)}`;
  }
  return `${fmt(c.dataInicio)} a ${fmt(c.dataFim)}`;
}


// O que a tela recusa gravar.
//
// Aceita o que o RESTO do sistema entende, e isso precisou ser corrigido:
// a fase alcançada já era lida pelo servidor, já aparecia na tela e já
// estava gravada em dezenas de lançamentos vindos da importação, mas esta
// função não a conhecia. Bastava abrir uma competição com "Fase de grupos"
// e mudar qualquer outra coisa para a gravação inteira ser recusada, por
// causa de uma linha que a própria tela exibia corretamente.
//
// Os códigos antigos também passam, pelo mesmo motivo.
function valorInvalido(v) {
  const texto = String(v || "").trim();
  if (!texto) return false;
  const lido = lerColocacao(texto);
  return Boolean(lido.invalido);
}

const VAZIA = { nome: "", dataInicio: "", dataFim: "", local: "" };

// ── O quadro de medalhas da modalidade ───────────────────────
//
// Uma coluna por COLOCAÇÃO premiada, e não três metais fixos mais um
// "outras".
//
// O pódio é cadastro da modalidade: três no tênis de mesa e no skate,
// cinco no BMX. Até 15/10/2026 o quadro desenhava ouro, prata e bronze e
// jogava o 4º e o 5º do BMX numa coluna chamada "outras", o que fazia
// duas posições que são pódio na modalidade parecerem sobra. Agora o
// desenho segue o cadastro: onde o pódio vai até o 5º, há cinco colunas.
//
// As colunas de contagem são as mesmas do relatório em PDF, na mesma
// ordem e com os mesmos nomes: pódios, provas, competições, aproveitamento
// e melhor. Tela e anexo precisam dizer a mesma coisa com as mesmas
// palavras, senão quem confere um contra o outro não sabe qual ler.
const METAL = { 1: "🥇 Ouro", 2: "🥈 Prata", 3: "🥉 Bronze" };

function colunasDePodio(tamanhoDoPodio) {
  const ate = Math.max(1, Number(tamanhoDoPodio) || 3);
  const cols = [];
  for (let i = 1; i <= ate; i += 1) cols.push({ posicao: i, rotulo: METAL[i] || `🏅 ${i}º` });
  return cols;
}

const TRES_METAIS = [1, 2, 3].map((posicao) => ({ posicao, rotulo: METAL[posicao] }));

// As colunas que o quadro REALMENTE mostra.
//
// Em geral são as do cadastro da modalidade. A exceção é o lançamento
// antigo, de antes da contagem posição a posição: ele não sabe dizer
// quantos quartos e quintos lugares houve, então o quadro cai para os três
// metais em vez de imprimir zero onde há medalha.
//
// A legenda pergunta a mesma função, e não o cadastro, senão ela
// prometeria cinco colunas sobre uma tabela de três.
function colunasEfetivas(ranking) {
  if (!ranking) return null;
  const temPorPosicao = (ranking.linhas || []).some((l) => l.porColocacao);
  return temPorPosicao ? colunasDePodio(ranking.tamanhoDoPodio) : TRES_METAIS;
}

// A legenda do pódio, escrita como se lê.
//
// Era uma frase fixa, "Ouro, prata, bronze, 4º e 5º", em todas as
// modalidades. O BMX premia até o quinto e estava certo; o skate e o tênis
// de mesa premiam até o terceiro, e a tela prometia duas posições que o
// quadro não tem. Agora sai do número de pódios cadastrado, qualquer que
// ele seja.
export function legendaDoPodio(colunas) {
  const nomes = colunas.map(({ posicao }) => {
    const metal = METAL[posicao];
    // Sem o emoji e em minúscula, porque aqui são palavras de uma frase.
    return metal ? metal.split(" ").slice(1).join(" ").toLowerCase() : `${posicao}º`;
  });
  const primeira = nomes[0].charAt(0).toUpperCase() + nomes[0].slice(1);
  const resto = nomes.slice(1);
  if (!resto.length) return `${primeira}.`;
  const ultima = resto.pop();
  const meio = [primeira, ...resto].join(", ");
  return `${meio} e ${ultima}.`;
}

function QuadroDaModalidade({ ranking }) {
  const totais = ranking.totais || {};
  const porPosicaoTotal = totais.porColocacao || {};

  // A mesma função que a legenda consulta, para as duas não divergirem.
  const colunas = colunasEfetivas(ranking);
  // O lançamento antigo também muda de onde vem o total, não só as
  // colunas: ele guardava ouro/prata/bronze somados, e não por posição.
  const temPorPosicao = (ranking.linhas || []).some((l) => l.porColocacao);
  const chaveAntiga = { 1: "ouro", 2: "prata", 3: "bronze" };

  const contagemDe = (linha, posicao) => (
    linha.porColocacao
      ? linha.porColocacao[posicao] ?? 0
      : (linha.medalhas || {})[chaveAntiga[posicao]] ?? 0
  );
  const totalDe = posicao => (
    temPorPosicao ? porPosicaoTotal[posicao] ?? 0 : totais[chaveAntiga[posicao]] ?? 0
  );
  const pct = v => (v == null ? "—" : `${String(v).replace(".", ",")}%`);

  const th = { padding: "8px 6px" };
  const td = { padding: "7px 6px", textAlign: "center" };

  return (
    <>
      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 680, fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#EEF2F7" }}>
            <th style={{ textAlign: "left", padding: "8px 10px" }}>Atleta</th>
            {colunas.map(c => (
              <th key={c.posicao} style={th}
                title={`Quantas vezes o atleta terminou em ${c.posicao}º lugar`}>
                {c.rotulo}
              </th>
            ))}
            <th style={th} title="Quantas vezes o atleta subiu ao pódio, que é o total de medalhas dele">
              Pódios
            </th>
            <th style={th} title="Categorias disputadas. O mesmo atleta pode disputar mais de uma categoria no mesmo evento">
              Provas
            </th>
            <th style={th} title="Eventos distintos de que o atleta participou">Competições</th>
            <th style={th} title="Pódios divididos por provas disputadas">Aprov.</th>
            <th style={th} title="A melhor colocação do atleta no período">Melhor</th>
          </tr>
        </thead>
        <tbody>
          {ranking.linhas.map((l, i) => (
            <tr key={l.uid} style={{ background: i % 2 ? "#F8FAFC" : BRANCO }}>
              <td style={{ padding: "7px 10px" }}>{l.nome}</td>
              {colunas.map(c => (
                <td key={c.posicao} style={td}>{contagemDe(l, c.posicao)}</td>
              ))}
              <td style={{ ...td, fontWeight: 800, color: AZUL_ESCURO }}>{l.totalMedalhas}</td>
              <td style={td}>{l.participacoes}</td>
              <td style={td}>{l.competicoes}</td>
              <td style={td}>{pct(l.aproveitamento)}</td>
              <td style={td}>{l.melhorColocacao ? `${l.melhorColocacao}º` : "—"}</td>
            </tr>
          ))}
          <tr style={{ background: "#EEF2F7", fontWeight: 800, color: AZUL_ESCURO }}>
            <td style={{ padding: "8px 10px" }}>TOTAL DA MODALIDADE</td>
            {colunas.map(c => (
              <td key={c.posicao} style={{ ...td, padding: "8px 6px" }}>{totalDe(c.posicao)}</td>
            ))}
            <td style={{ ...td, padding: "8px 6px" }}>{totais.total}</td>
            <td style={{ ...td, padding: "8px 6px" }}>{totais.participacoes}</td>
            <td style={{ ...td, padding: "8px 6px" }}>{totais.competicoes}</td>
            <td style={{ ...td, padding: "8px 6px" }}>{pct(totais.aproveitamento)}</td>
            <td />
          </tr>
        </tbody>
      </table>

      {/* A legenda saiu da tela a pedido da coordenação, que a achou longa
          demais para quem usa a tela todo dia. O que cada coluna quer dizer
          continua no título de cada uma, ao passar o cursor, e continua
          escrito por extenso no anexo em PDF, que é lido por quem não tem
          a tela na frente. */}
    </>
  );
}

// Acima de quantas categorias o campo deixa de ser seletor.
//
// No BMX e no skate são três ou quatro e o seletor é o melhor controle:
// abre, escolhe, fecha. No tênis de mesa são quase sessenta, e aí rolar
// uma lista desse tamanho com a folha do evento na mão é mais lento do
// que digitar. Acima do limite o campo passa a filtrar enquanto se digita,
// e continua aceitando o que não estiver na lista.
//
// O número não é configuração de ninguém: é o ponto em que a lista deixa
// de caber numa olhada.
// "Masc" ou "Fem" no nome da categoria, no começo de uma palavra.
// Montada por partes: barra invertida solta dentro de texto some em
// algumas ferramentas de edição, e a expressão quebrada casaria com
// qualquer coisa.
const MARCA_DE_GENERO = new RegExp("(^|[^A-Za-z])(masc|fem)", "i");

const LIMITE_DO_SELETOR = 12;

// A categoria de cada linha, como ela está neste instante.
//
// Serve de chave do agrupamento na tela. É tirado em dois momentos, ao
// abrir a competição e ao salvar, e nunca durante a digitação.
function retratoDoAgrupamento(lancamentos) {
  const retrato = {};
  Object.keys(lancamentos || {}).forEach((uid) => {
    retrato[uid] = (lancamentos[uid] || []).map((r) => String((r && r.categoria) || "").trim());
  });
  return retrato;
}

const ESTILO_DA_CATEGORIA = {
  width: 150, border: "1.5px solid #E5E7EB", borderRadius: 10,
  padding: "7px 9px", fontSize: 12.5, fontFamily: "inherit", color: CINZA_ESC,
};

// Lista curta vira seletor; lista longa vira campo que filtra enquanto se
// digita, pelo `datalist` do próprio navegador. Sem lista nenhuma, campo
// livre, que é o estado de quem ainda não cadastrou as categorias.
//
// As duas formas longas aceitam texto que não está na lista, de propósito:
// aparece categoria nova em competição, e travar o lançamento por causa de
// um nome que falta no cadastro seria pior do que a grafia divergente.
function CampoDeCategoria({ valor, onChange, opcoes, idDaLista, listaFechada }) {
  // Seletor só quando a modalidade TEM lista cadastrada e ela é curta.
  //
  // A distinção importa. Lista cadastrada em Núcleos é decisão da
  // coordenação sobre o que existe naquela modalidade, e aí escolher de um
  // seletor é o mais rápido. Lista derivada do que os atletas por acaso
  // têm no cadastro não é decisão de ninguém: virar seletor com ela
  // IMPEDE de digitar o que falta, que foi exatamente o que aconteceu
  // enquanto nenhum núcleo tinha categorias cadastradas.
  if (listaFechada && opcoes.length > 0 && opcoes.length <= LIMITE_DO_SELETOR) {
    return (
      <select value={valor} onChange={(e) => onChange(e.target.value)}
        style={{ ...ESTILO_DA_CATEGORIA, background: BRANCO, cursor: "pointer" }}>
        <option value="">categoria</option>
        {opcoes.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
    );
  }
  return (
    <>
      <input value={valor} onChange={(e) => onChange(e.target.value)}
        placeholder={opcoes.length ? "digite ou escolha" : "categoria"}
        list={opcoes.length ? idDaLista : undefined}
        style={ESTILO_DA_CATEGORIA} />
      {opcoes.length > 0 && (
        <datalist id={idDaLista}>
          {opcoes.map((c) => <option key={c} value={c} />)}
        </datalist>
      )}
    </>
  );
}

export default function SecaoResultados({ nucleo, nucleoNome, modalidades, modalidadeFixa = "" }) {
  const [modalidade, setModalidade] = useState(modalidadeFixa || "");
  // A lista do núcleo, buscada aqui quando ninguém a entrega.
  //
  // Era o defeito: a tela só carregava depois de ter uma modalidade
  // escolhida, e só escolhia a partir da lista que vinha por prop. Com a
  // prop vazia, nada carregava, e sem carregar a lista nunca chegava — o
  // seletor não aparecia e a tela ficava muda. Agora ela se vira sozinha.
  const [modalidadesDoNucleo, setModalidadesDoNucleo] = useState([]);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [status, setStatus] = useState(null);

  // Por qual categoria cada linha está AGRUPADA na tela.
  //
  // Tirado uma vez, ao abrir a competição, e refeito ao salvar. Nunca
  // durante a digitação, e esse é o ponto: o agrupamento é por categoria e
  // o título do grupo é a chave da lista no React. Seguindo o campo letra
  // a letra, digitar "Sub-19" trocava o grupo seis vezes, a linha era
  // desmontada e remontada a cada tecla e o cursor saía do campo. Era
  // preciso clicar de novo para digitar cada caractere.
  const [agrupamento, setAgrupamento] = useState({});

  const [competicaoId, setCompeticaoId] = useState(null);
  const [form, setForm] = useState(null);       // criação/edição da competição
  const [lancamentos, setLancamentos] = useState({});
  const [sujo, setSujo] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const [ranking, setRanking] = useState(null);
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");
  const [baixando, setBaixando] = useState(false);

  // As categorias cadastradas na modalidade. Carregadas mesmo quando a
  // modalidade está travada, que é o caso do técnico: é justamente ele quem
  // lança resultado, e sem a lista o campo voltaria a ser digitado à mão.
  const [categoriasNucleo, setCategoriasNucleo] = useState({});

  useEffect(() => {
    if (!nucleo) return undefined;
    let vivo = true;
    carregarCadastroDoNucleo(nucleo)
      .then((r) => { if (vivo) setCategoriasNucleo(r.categoriasPorModalidade || {}); })
      .catch(() => { if (vivo) setCategoriasNucleo({}); });
    return () => { vivo = false; };
  }, [nucleo]);

  useEffect(() => {
    if (!nucleo || modalidadeFixa) return undefined;
    let vivo = true;
    carregarModalidadesDoNucleo(nucleo)
      .then((lista) => { if (vivo) setModalidadesDoNucleo(lista || []); })
      .catch(() => { if (vivo) setModalidadesDoNucleo([]); });
    return () => { vivo = false; };
  }, [nucleo, modalidadeFixa]);

  const listaModalidades = useMemo(() => {
    if (modalidadeFixa) return [modalidadeFixa];
    if (modalidades && modalidades.length) return modalidades;
    if (modalidadesDoNucleo.length) return modalidadesDoNucleo;
    return (dados && dados.modalidades) || [];
  }, [modalidades, modalidadesDoNucleo, modalidadeFixa, dados]);

  useEffect(() => {
    if (modalidadeFixa) { setModalidade(modalidadeFixa); return; }
    if (!modalidade && listaModalidades.length) setModalidade(listaModalidades[0]);
    // Se a modalidade escolhida não pertence mais à lista (troca de núcleo),
    // volta para a primeira em vez de consultar uma modalidade que o núcleo
    // não tem.
    if (modalidade && listaModalidades.length && !listaModalidades.includes(modalidade)) {
      setModalidade(listaModalidades[0]);
    }
  }, [listaModalidades, modalidade, modalidadeFixa]);

  const recarregar = useCallback(async () => {
    if (!nucleo || !modalidade) return;
    setCarregando(true);
    try {
      const r = await listarCompeticoes(nucleo, modalidade);
      setDados(r);
    } catch (e) {
      setStatus({ tipo: "erro", txt: e.message || "Não foi possível carregar as competições." });
    } finally {
      setCarregando(false);
    }
  }, [nucleo, modalidade]);

  useEffect(() => { recarregar(); }, [recarregar]);

  // Trocar de modalidade fecha a competição aberta. Sem isso, a matriz
  // continuaria na tela com os atletas da modalidade nova e os resultados
  // da antiga, que é o tipo de confusão que vira lançamento errado.
  useEffect(() => {
    setCompeticaoId(null);
    setLancamentos({});
    setSujo(false);
    setRanking(null);
  }, [modalidade]);

  const competicoes = (dados && dados.competicoes) || [];
  // Em useMemo porque a lista entra como dependência do agrupamento por
  // categoria logo abaixo: recriada a cada render, o agrupamento refazia o
  // trabalho sempre, mesmo sem nada ter mudado.
  const aberta = competicoes.find((c) => c.id === competicaoId) || null;

  // Os atletas da MODALIDADE aberta, e só eles.
  //
  // O servidor já filtra quando a modalidade vem escolhida na consulta,
  // mas a tela não pode depender disso: basta a consulta sair sem
  // modalidade para a matriz de uma competição de BMX listar atleta de
  // tênis de mesa. E foi por aí que a categoria de uma modalidade vazou
  // para a lista da outra, porque a linha desse atleta nascia preenchida
  // com a categoria do cadastro dele.
  //
  // Sai quem é de OUTRA modalidade. Quem está sem modalidade no cadastro
  // fica: o servidor já recortou a consulta, e esconder esse atleta da
  // matriz seria pior do que mostrá-lo, porque ninguém conseguiria lançar
  // o resultado dele. A categoria dele também não vaza para a lista, que
  // só aceita o que tem a modalidade certa.
  const todosOsAtletas = useMemo(() => (dados && dados.atletas) || [], [dados]);
  const atletas = useMemo(() => {
    const alvo = chave((aberta && aberta.modalidade) || modalidade);
    if (!alvo) return todosOsAtletas;
    return todosOsAtletas.filter((a) => {
      const dele = chave(a.modalidade);
      return !dele || dele === alvo;
    });
  }, [todosOsAtletas, aberta, modalidade]);
  const tamanhoDoPodio = (dados && dados.tamanhoDoPodio) || 3;

  // As categorias oferecidas no lançamento: as cadastradas na modalidade
  // mais as que os atletas já têm.
  //
  // A união existe porque tirar uma categoria do cadastro não pode fazer
  // sumir a que está gravada em quem a disputou: a competição já aconteceu,
  // e o resultado dela continua valendo.
  const opcoesDaCompeticao = useMemo(() => {
    const daCompeticao = (aberta && aberta.modalidade) || modalidade;

    // O que está em uso, com a modalidade de cada um ao lado. A regra usa
    // essa modalidade para descartar o que é de outra: antes a lista
    // juntava a categoria de QUALQUER atleta do núcleo, e bastava um
    // atleta de tênis de mesa para o campo do BMX sugerir Sub-19.
    //
    // O lançamento herda a modalidade da competição aberta, porque é dela
    // que ele é.
    const emUso = [
      ...atletas.map((a) => ({ categoria: a.categoria, modalidade: a.modalidade })),
      ...Object.values(lancamentos).flatMap((lista) => (lista || []).map(
        (l) => ({ categoria: l && l.categoria, modalidade: daCompeticao })
      )),
    ];

    return categoriasDisponiveis({ mapa: categoriasNucleo, modalidade: daCompeticao, emUso });
  }, [categoriasNucleo, aberta, modalidade, atletas, lancamentos]);

  // As categorias CADASTRADAS da modalidade aberta, sem as derivadas.
  //
  // É esta, e não a soma, que decide a forma do campo e o preenchimento
  // automático: uma é decisão da coordenação, a outra é o que apareceu.
  const cadastradas = useMemo(
    () => categoriasDaModalidade(categoriasNucleo, (aberta && aberta.modalidade) || modalidade),
    [categoriasNucleo, aberta, modalidade],
  );

  // A modalidade separa as categorias por gênero?
  //
  // Sai do dado, e não do nome da modalidade: no tênis de mesa a lista tem
  // dupla masculina e feminina, e é isso que torna o gênero do atleta
  // necessário ao lado da categoria. Uma modalidade nova com a mesma
  // estrutura ganha o mesmo comportamento sem ninguém escrever o nome dela
  // em lugar nenhum.
  const separaPorGenero = useMemo(
    () => opcoesDaCompeticao.some((c) => MARCA_DE_GENERO.test(c)),
    [opcoesDaCompeticao],
  );


  function abrir(c) {
    setCompeticaoId(c.id);
    setForm(null);
    // O que já foi lançado vem da competição. Quem ainda não tem resultado
    // entra com a categoria do cadastro, que é o palpite certo na maioria
    // das vezes e pode ser corrigido na linha.
    const inicial = {};
    atletas.forEach((a) => {
      // O lançamento do atleta é uma LISTA: ele pode ter disputado mais de
      // uma categoria no mesmo evento. No tênis de mesa é a regra, com a
      // simples da idade, o absoluto e as duplas no mesmo fim de semana.
      //
      // Documento antigo guardava um objeto só, e `comoLista` aceita os
      // dois formatos, para a competição de antes abrir sem migração.
      const guardados = comoLista((c.resultados || {})[a.uid]);
      inicial[a.uid] = guardados.length
        ? guardados.map((r) => ({
          colocacao: r.colocacao || "",
          categoria: r.categoria || "",
          tipo: r.tipo || "",
        }))
        // A categoria do CADASTRO entra preenchida só onde ela é mesmo a
        // categoria da competição.
        //
        // No BMX e no skate é: o atleta disputa a dele e pronto, e já vir
        // preenchida poupa o lançamento inteiro. No tênis de mesa não é:
        // são quase sessenta categorias e o atleta disputa várias no mesmo
        // fim de semana, então a do cadastro é só a principal e vir
        // preenchida faz o técnico apagar antes de escrever a certa.
        : [{
          colocacao: "",
          categoria: cadastradas.length > LIMITE_DO_SELETOR ? "" : (a.categoria || ""),
          tipo: "principal",
        }];
    });
    setLancamentos(inicial);
    // O retrato do agrupamento, tirado agora. Enquanto ele não mudar, a
    // linha não sai do lugar, e é isso que mantém o cursor no campo.
    setAgrupamento(retratoDoAgrupamento(inicial));
    setSujo(false);
    setStatus(null);
  }

  function mudar(uid, i, campo, valor) {
    setLancamentos((l) => {
      const lista = [...(l[uid] || [])];
      lista[i] = { ...(lista[i] || {}), [campo]: valor };
      return { ...l, [uid]: lista };
    });
    setSujo(true);
  }

  // Mais uma categoria para o mesmo atleta no mesmo evento.
  //
  // A nova entra vazia e marcada como secundária, porque a principal é a
  // da idade dele e já está na primeira linha. Quem lança corrige se for
  // o contrário.
  function acrescentarCategoria(uid) {
    setLancamentos((l) => ({
      ...l,
      [uid]: [...(l[uid] || []), { colocacao: "", categoria: "", tipo: "secundaria" }],
    }));
    setSujo(true);
  }

  // Tirar uma categoria lançada por engano.
  //
  // A última não some: ela vira linha em branco. Atleta sem nenhuma linha
  // desapareceria da matriz, e aí não teria como lançar nada para ele.
  function tirarCategoria(uid, i) {
    setLancamentos((l) => {
      const lista = (l[uid] || []).filter((_, k) => k !== i);
      return { ...l, [uid]: lista.length ? lista : [{ colocacao: "", categoria: "", tipo: "principal" }] };
    });
    setSujo(true);
  }

  async function gravar() {
    const errados = Object.keys(lancamentos).filter((uid) =>
      (lancamentos[uid] || []).some((r) => valorInvalido(r.colocacao)));
    if (errados.length) {
      setStatus({
        tipo: "erro",
        txt: "Há colocação que não é número nem código conhecido. Use o número da colocação, ou NC, DC ou LE.",
      });
      return;
    }
    setSalvando(true);
    try {
      const r = await salvarResultados({ competicaoId, resultados: lancamentos });
      // Salvou: agora sim as linhas podem mudar de grupo, e é o momento
      // certo, porque quem salvou terminou de escrever.
      setAgrupamento(retratoDoAgrupamento(lancamentos));
      setStatus({ tipo: "ok", txt: `Resultados salvos. ${r.lancados} atleta(s) com resultado nesta competição.` });
      setSujo(false);
      await recarregar();
    } catch (e) {
      setStatus({ tipo: "erro", txt: e.message || "Não foi possível salvar." });
    } finally {
      setSalvando(false);
    }
  }

  async function gravarCompeticao() {
    if (!form.nome.trim() || !form.dataInicio) {
      setStatus({ tipo: "erro", txt: "Informe pelo menos o nome e a data de início." });
      return;
    }
    setSalvando(true);
    try {
      await salvarCompeticao({ ...form, nucleo, modalidade, competicaoId: form.id || null });
      setStatus({ tipo: "ok", txt: form.id ? "Competição atualizada." : "Competição criada. Agora lance os resultados." });
      setForm(null);
      await recarregar();
    } catch (e) {
      setStatus({ tipo: "erro", txt: e.message || "Não foi possível salvar a competição." });
    } finally {
      setSalvando(false);
    }
  }

  async function excluir(c) {
    const quantos = Object.keys(c.resultados || {}).length;
    const aviso = quantos
      ? `Excluir "${c.nome}"? Os ${quantos} resultados lançados nela serão apagados e saem do quadro de medalhas.`
      : `Excluir "${c.nome}"?`;
    if (!window.confirm(aviso)) return;
    try {
      await removerCompeticao(c.id);
      if (competicaoId === c.id) setCompeticaoId(null);
      setStatus({ tipo: "ok", txt: "Competição excluída." });
      await recarregar();
    } catch (e) {
      setStatus({ tipo: "erro", txt: e.message || "Não foi possível excluir." });
    }
  }

  async function verRanking() {
    try {
      const r = await rankingDeMedalhas({ nucleo, modalidade, de: de || null, ate: ate || null });
      setRanking(r);
    } catch (e) {
      setStatus({ tipo: "erro", txt: e.message || "Não foi possível montar o quadro." });
    }
  }

  async function baixarPDF() {
    setBaixando(true);
    try {
      const r = ranking || await rankingDeMedalhas({ nucleo, modalidade, de: de || null, ate: ate || null });
      setRanking(r);
      await gerarPDFCompeticoes({ ranking: r, nucleo, nucleoNome, de: de || null, ate: ate || null });
    } catch (e) {
      setStatus({ tipo: "erro", txt: e.message || "Não foi possível gerar o relatório." });
    } finally {
      setBaixando(false);
    }
  }

  // Atletas agrupados por categoria.
  //
  // A categoria vem do AGRUPAMENTO, que é um retrato tirado ao abrir a
  // competição, e não do campo que está sendo digitado. A linha fica onde
  // está enquanto se escreve, e muda de grupo quando se salva.
  const grupos = useMemo(() => {
    const mapa = new Map();
    atletas.forEach((a) => {
      // Uma LINHA por categoria disputada, e não por atleta. O mesmo
      // nome aparece em quantos grupos ele tiver inscrição, que é como a
      // folha de resultados chega: uma categoria de cada vez.
      (lancamentos[a.uid] || [{}]).forEach((r, i) => {
        const cat = String(((agrupamento[a.uid] || [])[i]) || "").trim();
        const k = chave(cat) || "__sem__";
        if (!mapa.has(k)) mapa.set(k, { titulo: cat || "Sem categoria lançada", atletas: [] });
        mapa.get(k).atletas.push({ ...a, linha: i });
      });
    });
    return [...mapa.values()].sort((a, b) => {
      if (a.titulo.startsWith("Sem categoria")) return 1;
      if (b.titulo.startsWith("Sem categoria")) return -1;
      return a.titulo.localeCompare(b.titulo, "pt-BR");
    });
  }, [atletas, lancamentos, agrupamento]);

  // Atletas com pelo menos uma categoria lançada, e o total de categorias.
  // Os dois aparecem porque "12 atletas" e "31 resultados" respondem
  // perguntas diferentes quando um atleta disputa cinco provas.
  const lancados = Object.keys(lancamentos)
    .filter((u) => (lancamentos[u] || []).some((r) => String(r.colocacao || "").trim())).length;
  const categoriasLancadas = Object.values(lancamentos)
    .reduce((n, lista) => n + (lista || []).filter((r) => String(r.colocacao || "").trim()).length, 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Card>
        <div style={{ fontWeight: 800, fontSize: 18, color: TEXTO }}>Resultados de competição</div>
        <div style={{ fontSize: 13, color: CINZA_ESC, marginTop: 6, lineHeight: 1.6 }}>
          Cadastre a competição, lance a colocação de cada atleta.
        </div>

        {/* O técnico entra com a modalidade dele travada: o seletor
            ofereceria uma escolha que o servidor recusa. */}
        {!modalidadeFixa && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
          {listaModalidades.map((m) => (
            <button key={m} onClick={() => setModalidade(m)}
              style={{
                border: `1.5px solid ${chave(m) === chave(modalidade) ? AZUL : "#D1D5DB"}`,
                background: chave(m) === chave(modalidade) ? AZUL : BRANCO,
                color: chave(m) === chave(modalidade) ? BRANCO : TEXTO,
                borderRadius: 8, padding: "7px 14px", fontSize: 13, fontWeight: 700,
                cursor: "pointer", fontFamily: "inherit",
              }}>
              {m}
            </button>
          ))}
        </div>
        )}

        {modalidade && (
          <div style={{ fontSize: 12, color: CINZA_ESC, marginTop: 10 }}>
            Pódio do {modalidade}: até o <b>{tamanhoDoPodio}º</b> colocado.
          </div>
        )}
      </Card>

      {status && <StatusBox tipo={status.tipo} msg={status.txt} />}

      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 15.5, color: TEXTO }}>
            Competições de {modalidade || "—"}
          </div>
          {!form && (
            <Btn onClick={() => setForm({ ...VAZIA })}>+ Nova competição</Btn>
          )}
        </div>

        {form && (
          <div style={{ marginTop: 14, background: AZUL_CLARO, borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: AZUL_ESCURO }}>
              {form.id ? "Editar competição" : "Nova competição"}
            </div>
            <input placeholder="Nome da competição" value={form.nome}
              onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
              style={{ border: "1px solid #D1D5DB", borderRadius: 8, padding: "9px 11px", fontSize: 13.5, fontFamily: "inherit" }} />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <label style={{ fontSize: 12.5, color: CINZA_ESC }}>
                Início<br />
                <input type="date" value={form.dataInicio}
                  onChange={(e) => setForm((f) => ({ ...f, dataInicio: e.target.value }))}
                  style={{ border: "1px solid #D1D5DB", borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: "inherit" }} />
              </label>
              <label style={{ fontSize: 12.5, color: CINZA_ESC }}>
                Término (deixe vazio se for de um dia só)<br />
                <input type="date" value={form.dataFim}
                  onChange={(e) => setForm((f) => ({ ...f, dataFim: e.target.value }))}
                  style={{ border: "1px solid #D1D5DB", borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: "inherit" }} />
              </label>
              <label style={{ fontSize: 12.5, color: CINZA_ESC, flex: 1, minWidth: 180 }}>
                Local<br />
                <input value={form.local}
                  onChange={(e) => setForm((f) => ({ ...f, local: e.target.value }))}
                  style={{ width: "100%", border: "1px solid #D1D5DB", borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" }} />
              </label>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn onClick={gravarCompeticao} disabled={salvando}>
                {salvando ? "Salvando..." : "Salvar competição"}
              </Btn>
              <Btn variant="secondary" onClick={() => setForm(null)}>Cancelar</Btn>
            </div>
          </div>
        )}

        {carregando && <div style={{ fontSize: 13, color: CINZA_ESC, marginTop: 12 }}>Carregando...</div>}

        {!carregando && !competicoes.length && !form && (
          <div style={{ fontSize: 13, color: CINZA_ESC, marginTop: 12, lineHeight: 1.6 }}>
            Nenhuma competição cadastrada nesta modalidade ainda. As competições previstas no
            planejamento do trimestre podem ser cadastradas aqui quando acontecerem.
          </div>
        )}

        {competicoes.length > 0 && (
          <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
            {competicoes.map((c) => {
              const ativa = c.id === competicaoId;
              const quantos = Object.keys(c.resultados || {}).length;
              return (
                <div key={c.id}
                  style={{
                    border: `1.5px solid ${ativa ? AZUL : "#E5E7EB"}`,
                    background: ativa ? AZUL_CLARO : BRANCO,
                    borderRadius: 10, padding: "11px 13px",
                    display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                  }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: TEXTO }}>{c.nome}</div>
                    <div style={{ fontSize: 12, color: CINZA_ESC, marginTop: 2 }}>
                      {periodoDe(c)}{c.local ? ` · ${c.local}` : ""} · {quantos} resultado(s)
                    </div>
                  </div>
                  <Btn onClick={() => abrir(c)}>{ativa ? "Lançando" : "Lançar resultados"}</Btn>
                  <button onClick={() => setForm({ ...c, dataFim: c.dataFim === c.dataInicio ? "" : c.dataFim, local: c.local || "" })}
                    title="Editar competição"
                    style={{ background: AZUL_CLARO, border: "none", borderRadius: 10, padding: "6px 12px", fontSize: 12, color: AZUL, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    Editar
                  </button>
                  <button onClick={() => excluir(c)}
                    title="Excluir competição"
                    style={{ background: "none", border: "none", borderRadius: 10, padding: "6px 8px", fontSize: 15, color: "#CBD5E1", cursor: "pointer", fontFamily: "inherit" }}>
                    🗑️
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {aberta && (
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: TEXTO }}>{aberta.nome}</div>
              <div style={{ fontSize: 12.5, color: CINZA_ESC, marginTop: 3 }}>
                {periodoDe(aberta)}{aberta.local ? ` · ${aberta.local}` : ""} · {lancados} de {atletas.length} atleta(s)
                {categoriasLancadas !== lancados && ` · ${categoriasLancadas} resultados`}
              </div>
            </div>
            <Btn onClick={gravar} disabled={salvando || !sujo}>
              {salvando ? "Salvando..." : sujo ? "Salvar resultados" : "Tudo salvo"}
            </Btn>
          </div>

          <div style={{ fontSize: 12, color: CINZA_ESC, marginTop: 10, lineHeight: 1.6, background: "#F8FAFC", borderRadius: 8, padding: "10px 12px" }}>
            Quem não tem colocação recebe um código: <b>NC</b> não classificou,
            {" "}<b>ND</b> não disputou. <b>NC</b> conta como participação na competição,
            {" "}<b>ND</b> não.
          </div>

          {!atletas.length && (
            <div style={{ fontSize: 13, color: CINZA_ESC, marginTop: 14 }}>
              Nenhum atleta ativo cadastrado em {modalidade}.
            </div>
          )}

          {grupos.map((g) => (
            <div key={g.titulo} style={{ marginTop: 18 }}>
              <div style={{
                fontSize: 12.5, fontWeight: 800, color: AZUL_ESCURO, textTransform: "uppercase",
                letterSpacing: 0.4, borderBottom: `2px solid ${AZUL_CLARO}`, paddingBottom: 5,
              }}>
                {g.titulo}
              </div>

              {g.atletas.map((a) => {
                const i = a.linha;
                const todas = lancamentos[a.uid] || [];
                const l = todas[i] || { colocacao: "", categoria: "", tipo: "" };
                const med = medalhaDe(l.colocacao, tamanhoDoPodio);
                const ruim = valorInvalido(l.colocacao);
                return (
                  <div key={`${a.uid}-${i}`} style={{
                    display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                    padding: "9px 2px", borderBottom: "1px solid #F1F5F9",
                  }}>
                    <div style={{ flex: 1, minWidth: 170 }}>
                      <div style={{ fontSize: 13.5, color: TEXTO }}>
                        {a.nome}
                        {/* O desligado só aparece aqui porque já tem
                            resultado nesta competição. A marca existe
                            para ninguém achar que ele voltou ao
                            programa, e para quem estiver lançando
                            entender por que o nome está na lista. */}
                        {a.desligado && (
                          <span style={{
                            marginLeft: 7, fontSize: 10.5, fontWeight: 700, color: CINZA_ESC,
                            border: "1px solid #E5E7EB", borderRadius: 5, padding: "1px 5px",
                          }}>
                            desligado
                          </span>
                        )}
                      </div>
                      {a.idade !== null && a.idade !== undefined && (
                        <div style={{ fontSize: 11.5, color: CINZA_ESC }}>{a.idade} anos</div>
                      )}
                    </div>

                    {/* O campo muda de forma conforme o tamanho da lista.
                        A principal já vem preenchida do cadastro do atleta;
                        este campo é o que muda quando ele disputa outra
                        categoria no mesmo evento.

                        As opções vêm ordenadas PARA ESTE ATLETA: a faixa
                        da idade dele na frente, e sem as do gênero oposto.
                        Nada mais some, porque ele sobe de categoria, joga
                        absoluto e entra em equipe. */}
                    <CampoDeCategoria
                      valor={l.categoria}
                      onChange={(v) => mudar(a.uid, i, "categoria", v)}
                      opcoes={ordenarParaAtleta(opcoesDaCompeticao, { idade: a.idade, sexo: a.sexo })}
                      listaFechada={cadastradas.length > 0}
                      idDaLista={`cats-${a.uid}-${i}`}
                    />

                    {/* O gênero do atleta, fixo ao lado da categoria.
                        Só onde a lista separa por gênero: no tênis de mesa
                        existem Sub-15 masculino e feminino com o mesmo
                        nome de faixa, e sem isto a linha não diz qual é.
                        No BMX e no skate a categoria já carrega o gênero
                        no próprio nome, e repetir seria ruído. */}
                    {separaPorGenero && (
                      <span title="Gênero do atleta, do cadastro" style={{
                        fontSize: 11, fontWeight: 700, color: CINZA_ESC,
                        background: FUNDO_SUAVE, border: `1px solid ${BORDA}`,
                        borderRadius: 6, padding: "4px 7px", whiteSpace: "nowrap",
                      }}>
                        {a.sexo === "feminino" ? "Fem" : a.sexo === "masculino" ? "Masc" : "sem gênero"}
                      </span>
                    )}

                    <input value={l.colocacao}
                      onChange={(e) => mudar(a.uid, i, "colocacao", e.target.value)}
                      placeholder="col."
                      style={{
                        width: 62, textAlign: "center", fontWeight: 700,
                        border: `1.5px solid ${ruim ? PERIGO : "#D1D5DB"}`, borderRadius: 10,
                        padding: "7px 9px", fontSize: 13.5, fontFamily: "inherit",
                      }} />

                    <div style={{ width: 96, fontSize: 12, color: med ? AZUL_ESCURO : CINZA_ESC, fontWeight: med ? 700 : 400 }}>
                      {ruim ? <span style={{ color: PERIGO }}>não entendi</span>
                        : med ? `${med.sinal} ${med.txt}`
                        /* Pela peça compartilhada, e não pela lista de
                           botões: ela conhece a fase alcançada e os
                           códigos antigos, que a lista não oferece mas o
                           sistema continua aceitando. */
                        : lerColocacao(l.colocacao).posicao === null ? rotuloDaColocacao(l.colocacao)
                        : ""}
                    </div>

                    <div style={{ display: "flex", gap: 4 }}>
                      {SITUACOES.map((s) => (
                        <button key={s.cod} title={s.rotulo}
                          onClick={() => mudar(a.uid, i, "colocacao", l.colocacao === s.cod ? "" : s.cod)}
                          style={{
                            border: `1.5px solid ${l.colocacao === s.cod ? AZUL : "#E5E7EB"}`,
                            background: l.colocacao === s.cod ? AZUL : BRANCO,
                            color: l.colocacao === s.cod ? BRANCO : CINZA_ESC,
                            borderRadius: 6, padding: "5px 8px", fontSize: 11.5, fontWeight: 700,
                            cursor: "pointer", fontFamily: "inherit",
                          }}>
                          {s.cod}
                        </button>
                      ))}
                    </div>

                    {/* Principal é a categoria da idade do atleta;
                        secundária é idade acima, absoluto ou dupla. A
                        distinção separa "como ele vai no nível dele" de
                        "quanto ele competiu", e sem ela quem aceita subir
                        de categoria aparece pior no relatório do que quem
                        só corre no nível dele. */}
                    <div style={{ display: "flex", gap: 4 }}>
                      {TIPOS.map((t) => (
                        <button key={t.cod} title={t.ajuda}
                          onClick={() => mudar(a.uid, i, "tipo", l.tipo === t.cod ? "" : t.cod)}
                          style={{
                            border: `1.5px solid ${l.tipo === t.cod ? AZUL_ESCURO : "#E5E7EB"}`,
                            background: l.tipo === t.cod ? AZUL_CLARO : BRANCO,
                            color: l.tipo === t.cod ? AZUL_ESCURO : CINZA_ESC,
                            borderRadius: 6, padding: "5px 9px", fontSize: 11.5, fontWeight: 700,
                            cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                          }}>
                          {t.rotulo}
                        </button>
                      ))}
                    </div>

                    <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
                      <button title="Outra categoria para este atleta nesta competição"
                        onClick={() => acrescentarCategoria(a.uid)}
                        style={BOTAO_DE_LINHA}>+ categoria</button>
                      {todas.length > 1 && (
                        <button title="Tirar esta categoria"
                          onClick={() => tirarCategoria(a.uid, i)}
                          style={{ ...BOTAO_DE_LINHA, color: PERIGO, borderColor: "#FCA5A5" }}>
                          remover
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          <datalist id="categorias-resultados">
            {opcoesDaCompeticao.map((c) => <option key={c} value={c} />)}
          </datalist>

          {atletas.length > 0 && (
            <div style={{ marginTop: 16, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <Btn onClick={gravar} disabled={salvando || !sujo}>
                {salvando ? "Salvando..." : sujo ? "Salvar resultados" : "Tudo salvo"}
              </Btn>
              {sujo && (
                <span style={{ fontSize: 12.5, color: PERIGO }}>
                  Há alterações que ainda não foram salvas.
                </span>
              )}
            </div>
          )}
        </Card>
      )}

      <Card>
        <div style={{ fontWeight: 700, fontSize: 15.5, color: TEXTO }}>
          Quadro de medalhas de {modalidade || "—"}
        </div>
        <div style={{ fontSize: 12.5, color: CINZA_ESC, marginTop: 5, lineHeight: 1.6 }}>
          O quadro segue o critério olímpico.{" "}
          {legendaDoPodio(colunasEfetivas(ranking) || colunasDePodio(tamanhoDoPodio))}
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginTop: 12 }}>
          <label style={{ fontSize: 12.5, color: CINZA_ESC }}>
            De<br />
            <input type="date" value={de} onChange={(e) => setDe(e.target.value)}
              style={{ border: "1px solid #D1D5DB", borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: "inherit" }} />
          </label>
          <label style={{ fontSize: 12.5, color: CINZA_ESC }}>
            Até<br />
            <input type="date" value={ate} onChange={(e) => setAte(e.target.value)}
              style={{ border: "1px solid #D1D5DB", borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: "inherit" }} />
          </label>
          <Btn onClick={verRanking}>Ver quadro</Btn>
          <Btn variant="secondary" onClick={baixarPDF} disabled={baixando}>
            {baixando ? "Gerando..." : "📄 Baixar relatório"}
          </Btn>
        </div>

        {ranking && (
          <div style={{ marginTop: 16, overflowX: "auto" }}>
            {!ranking.linhas.length ? (
              <div style={{ fontSize: 13, color: CINZA_ESC }}>
                Nenhum resultado lançado nesta modalidade no período escolhido.
              </div>
            ) : (
              <QuadroDaModalidade ranking={ranking} />
            )}
          </div>
        )}
      </Card>

      <div style={{ height: 8, borderBottom: `3px solid ${AMARELO}`, opacity: 0.35 }} />
    </div>
  );
}
